import {
  PartnerOrdersSummaryQuery,
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsQuery,
} from "@/types/partner-revenue-analytics";
import type { HotelPartnerOrdersSummary } from "@/types/hotelService";
import {
  PartnerOrdersQuery,
  PartnerOrdersResponse,
} from "@/types/partner-order";
import type {
  PartnerAttributionQuery,
  PartnerAttributionReport,
} from "@/types/partner-attribution";
import type {
  PartnerQrMetrics,
  PartnerQrMetricsQuery,
} from "@/types/partner-qr-metrics";
import {
  PartnerDestination,
  PartnerSettings,
  UpdatePartnerSettingsRequest,
  PartnerServicesListResponse,
  PartnerServiceFilterOptions,
  PartnerServicesFilters,
} from "@/types/partner-settings";
import { BaseService } from "./BaseService";

class PartnerSettingsService extends BaseService {
  async getPartnerSettings(hotelId: string): Promise<PartnerSettings> {
    return this.get<PartnerSettings>(`/hotels/${hotelId}/partners/settings`);
  }

  async getPartnerOrdersSummary(
    hotelId: string,
    query: PartnerOrdersSummaryQuery,
  ): Promise<HotelPartnerOrdersSummary> {
    return this.get<HotelPartnerOrdersSummary>(
      `/hotels/${hotelId}/partners/orders/summary`,
      query,
    );
  }

  // Individual order rows (contract 2026-09-27) — held back from `orders/summary`,
  // supports the in/out-of-catalog filter. Toast disabled: the consuming
  // table shows its own friendly inline error (sim found a raw error body
  // rendered under the list, 2026-09-27) rather than a toast built from the
  // response.
  async getPartnerOrders(
    hotelId: string,
    query: PartnerOrdersQuery,
  ): Promise<PartnerOrdersResponse> {
    return this.get<PartnerOrdersResponse>(
      `/hotels/${hotelId}/partners/orders`,
      query,
      { disableToast: true },
    );
  }

  // "Activities by room" report (task 0041, contract 2026-09-30) — hotel
  // staff and tenant admin share this same hotel-scoped endpoint.
  async getPartnerAttribution(
    hotelId: string,
    query: PartnerAttributionQuery,
  ): Promise<PartnerAttributionReport> {
    return this.get<PartnerAttributionReport>(
      `/hotels/${hotelId}/partners/attribution`,
      query,
    );
  }

  async getPartnerQrMetrics(
    hotelId: string,
    query: PartnerQrMetricsQuery = {},
  ): Promise<PartnerQrMetrics> {
    return this.get<PartnerQrMetrics>(
      `/hotels/${hotelId}/partners/qr-metrics`,
      query,
    );
  }

  async getPartnerRevenueAnalytics(
    hotelId: string,
    query: PartnerRevenueAnalyticsQuery,
  ): Promise<PartnerRevenueAnalyticsApiResponse> {
    return this.get<PartnerRevenueAnalyticsApiResponse>(
      `/hotels/${hotelId}/partners/orders/revenue-analytics`,
      query,
    );
  }

  /** From the city cache; `remote` asks the provider when the cache has no match ("City search"). */
  async searchDestinations(
    hotelId: string,
    query: string,
    remote = false,
  ): Promise<PartnerDestination[]> {
    return this.get<PartnerDestination[]>(
      `/hotels/${hotelId}/partners/destinations/search`,
      remote ? { query, remote: true } : { query },
    );
  }

  // `triggerSync`/`getSyncStatus`/`getPartnerLogs` at hotel scope removed
  // (task 0036, contract 2026-09-29): `/hotels/:hotelId/partners/sync(/status)`
  // and `/hotels/:hotelId/partners/logs` now 404. Sync is Guesteria-admin-only
  // — see `adminPartnersService`'s `triggerHotelSync`/`getHotelSyncStatus`/
  // `updateHotelAutoSync`/`getPartnerLogs`.

  async updatePartnerSettings(
    hotelId: string,
    data: UpdatePartnerSettingsRequest,
  ): Promise<PartnerSettings> {
    return this.put<PartnerSettings>(
      `/hotels/${hotelId}/partners/settings`,
      data,
    );
  }

  // Task 0028 — the single "Guesteria Partners" toggle. HOTEL_ADMIN and
  // TENANT_ADMIN only; the backend enforces the role, FRONT_DESK's read-only
  // view just never calls these. Contract 2026-09-28.
  async enablePartnerSettings(hotelId: string): Promise<PartnerSettings> {
    return this.patch<PartnerSettings>(
      `/hotels/${hotelId}/partners/settings/enable`,
    );
  }

  async disablePartnerSettings(hotelId: string): Promise<PartnerSettings> {
    return this.patch<PartnerSettings>(
      `/hotels/${hotelId}/partners/settings/disable`,
    );
  }

  async getPartnerServices(
    hotelId: string,
    filters?: PartnerServicesFilters,
  ): Promise<PartnerServicesListResponse> {
    return this.get<PartnerServicesListResponse>(
      `/hotels/${hotelId}/partners/services`,
      filters,
    );
  }

  async getPartnerServicesFilterOptions(
    hotelId: string,
  ): Promise<PartnerServiceFilterOptions> {
    return this.get<PartnerServiceFilterOptions>(
      `/hotels/${hotelId}/partners/services/filter-options`,
    );
  }
}

export const partnerSettingsService = new PartnerSettingsService();
